<script>
  import { onMount } from 'svelte';

  import { Button, DataTable, InlineNotification, TooltipDefinition, TooltipIcon } from 'carbon-components-svelte';
  import { useNavigate } from 'svelte-navigator';

  import EventSchedule16 from 'carbon-icons-svelte/lib/EventSchedule16';
  import Play16 from 'carbon-icons-svelte/lib/Play16';
  import Warning16 from 'carbon-icons-svelte/lib/WarningAlt16';

  import { IndependentSystemTasks } from '../../../server/config/constants';

  import LoadingSpinner from '../../components/LoadingSpinner.svelte';
  import { getTasks, triggerTask } from '../../services';
  import { messagesStore } from '../../stores';

  let pageData = { loading: true };

  const navigate = useNavigate();

  onMount(async () => {
    try {
      const tasks = await getTasks();
      pageData = {
        // TODO: do we need SFTP05 sync task?
        tasks: tasks.filter(({ id }) => ![IndependentSystemTasks.DATA_INVENTORY_SYNC_SFTP05].includes(id)),
        loading: false,
      };
    } catch (error) {
      console.error('[TaskList] Failed to load tasks!', error);
      pageData = { loading: false, error };
    }
  });

  async function runTask(taskId, triggerButton) {
    pageData = { ...pageData, loading: true };
    try {
      await triggerTask(taskId);
      triggerButton.blur();
      navigate('/tasks#executions');
      pageData = { ...pageData, loading: false };
    } catch (error) {
      console.error('[TaskList] Failed to trigger!', error);
      pageData = { ...pageData, error, loading: false };
    }
  }

  $: failedTasks = $messagesStore.failedTaskExecutions?.map(({ taskId }) => taskId);

  $: tasksWithWarning = pageData.tasks?.map((task) => {
    const isFailedTaskWarning = failedTasks?.includes(task.id) && Object.values(IndependentSystemTasks).includes(task.id);
    return { ...task, showWarning: isFailedTaskWarning };
  });
</script>

{#if pageData.error}
  <InlineNotification hideCloseButton kind="error" lowContrast title="Error:" subtitle="Failed to load tasks! Please try again later." />
{:else}
  <LoadingSpinner loading={pageData.loading}>
    <DataTable
      class="mst-table--lock-end"
      headers={[
        { key: 'name', value: 'Name' },
        { key: 'actions', empty: true, sort: false },
      ]}
      rows={tasksWithWarning}
      sortable
    >
      <span slot="cell" let:cell let:row>
        {#if cell.key === 'actions' && row.manualExecutionEnabled}
          <Button size="small" kind="ghost" on:click={(event) => runTask(row.id, event.currentTarget)} icon={Play16}>Run</Button>
        {:else if cell.key === 'actions' && !row.manualExecutionEnabled}
          <!-- Don't render anything -->
        {:else if cell.display}
          {cell.display(cell.value, row)}
        {:else if cell.key === 'name'}
          <div class="name-cell">
            {#if row.frequencyDescription}
              <TooltipIcon direction="right" tooltipText={row.frequencyDescription} icon={EventSchedule16} portalTooltip />
            {/if}
            {#if row.description}
              <TooltipDefinition tooltipText={row.description} portalTooltip>{cell.value}</TooltipDefinition>
            {:else}
              {cell.value}
            {/if}
            {#if row.showWarning}
              <div class="failed-task-warning-icon">
                <TooltipIcon tooltipText="This task has failed in the last 24 hours!" icon={Warning16} portalTooltip />
              </div>
            {/if}
          </div>
        {/if}
      </span>
    </DataTable>
  </LoadingSpinner>
{/if}

<style>
  .failed-task-warning-icon {
    display: flex;
  }

  .failed-task-warning-icon :global(svg) {
    fill: var(--cds-support-warning-inverse);
  }

  .name-cell {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    white-space: nowrap;
    font-size: var(--cds-label-01-font-size);
  }
</style>
